import React, { useEffect, useState } from 'react';
import { useSelector } from "react-redux";
import { Row, Col, Modal, Form, FormGroup, FormLabel, FormControl, Button } from 'react-bootstrap';
import { updateAddress } from "../../../actions/addressActions";
import Loader from '../../Loader';
import Message from '../../Message';
import UpdateProfileForm from '../../UpdateProfileForm';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css'
import { addressUpdateReset } from '../../../reducers/addressReducers';
import DeleteAddressModal from './DeleteAddressModal';
import { FaPen, FaTrash, FaExclamationCircle } from 'react-icons/fa';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';
import { COLORS } from '../../../constants/colors';

// Import the necessary packages for country code conversion
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
countries.registerLocale(enLocale);

function UpdateAddressModal({ show, onHide, dispatch, addressData }) {
    // Color constants to match with ProfileDetails.js
    const plum = COLORS.PLUM;
    const sageGreen = COLORS.SAGE_GREEN;
    const errorColor = COLORS.ERROR;
    const warmConcrete = COLORS.WARM_CONCRETE;
    
    const [addressName, setAddressName] = useState('');
    const [mobile, setMobile] = useState('');
    const [province, setProvince] = useState('');
    const [district, setDistrict] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [country, setCountry] = useState('South Africa'); // Default to South Africa
    
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // Form validation states
    const [validated, setValidated] = useState(false);
    const [formErrors, setFormErrors] = useState({
        addressName: false,
        mobile: false,
        province: false,
        district: false,
        postalCode: false,
        country: false
    });

    const {
        addressReducers: { addressUpdateError, addressUpdateLoading, addressUpdateSuccess }
    } = useSelector((state) => state);

    // Helper function to get ISO 2-letter country code for phone input
    const getCountryIsoCode = (countryName) => {
        try {
            return countries.getAlpha2Code(countryName, 'en')?.toLowerCase() || 'za';
        } catch (error) {
            console.error('Error getting country code:', error);
            return 'za'; // Default to South Africa
        }
    };

    useEffect(() => {
        if (addressData) {
            setAddressName(addressData.name || '');
            setMobile(addressData.mobile || '');
            setProvince(addressData.province || '');
            setDistrict(addressData.district || '');
            setPostalCode(addressData.postalCode || '');
            setCountry(addressData.country || 'South Africa');
            // Reset validation state when form is populated
            setValidated(false);
            setFormErrors({
                addressName: false,
                mobile: false,
                province: false,
                district: false,
                postalCode: false,
                country: false
            });
        }
    }, [addressData, show]);

    useEffect(() => {
        if (addressUpdateSuccess) {
            dispatch(addressUpdateReset());
            onHide();
        }
    }, [dispatch, addressUpdateSuccess, onHide]);
    
    const validateForm = () => {
        const errors = {
            addressName: !addressName.trim(),
            mobile: !mobile.trim(),
            province: !province.trim(),
            district: !district.trim(),
            postalCode: !postalCode.trim(),
            country: !country.trim()
        };
        
        setFormErrors(errors);
        return !Object.values(errors).some(Boolean);
    };

    const submitHandler = (e) => {
        e.preventDefault();
        
        // Mark form as validated to show validation feedback
        setValidated(true);
        
        // Check if form is valid
        if (!validateForm()) {
            return;
        }

        const newAddressData = {
            ...addressData,
            _id: addressData._id,
            name: addressName.trim(),
            province: province.trim(),
            district: district.trim(),
            postalCode: postalCode.trim(),
            mobile: mobile.trim(),
            country: country.trim()
        };

        dispatch(updateAddress(newAddressData));
    };

    const showDelete = () => {
        onHide(); // Close update modal
        setShowDeleteModal(true); // Show delete modal
    };
    
    // Helper function to determine input border color
    const getBorderStyle = (fieldName) => {
        if (!validated) return {};
        return formErrors[fieldName] ? { borderColor: errorColor } : { borderColor: 'green' };
    };

    return (
        <>
            <Modal show={show} onHide={onHide}>
                <Modal.Header closeButton className='border-0'></Modal.Header>
                <Col className='d-flex justify-content-center'><h4 style={{ color: plum }}>Update Address</h4></Col>
                <Modal.Body>
                    <UpdateProfileForm>
                        {addressUpdateError && <Message variant='danger'>{addressUpdateError}</Message>}
                        {addressUpdateLoading && <Loader />}
                        <Form noValidate validated={validated} onSubmit={submitHandler}>
                            <FormGroup controlId='addressName'>
                                <FormLabel className='my-2 fw-semibold'>Address Name</FormLabel>
                                <FormControl
                                    required
                                    type='text'
                                    placeholder='Enter Address Name'
                                    value={addressName}
                                    onChange={(e) => setAddressName(e.target.value)}
                                    isInvalid={validated && formErrors.addressName}
                                    isValid={validated && !formErrors.addressName}
                                    style={getBorderStyle('addressName')}
                                />
                                <Form.Control.Feedback type="invalid">
                                    <div className="d-flex align-items-center">
                                        <FaExclamationCircle className="me-1" /> Address name is required
                                    </div>
                                </Form.Control.Feedback>
                            </FormGroup>

                            <FormGroup controlId='country'>
                                <FormLabel className='my-2 fw-semibold'>Country</FormLabel>
                                <CountryDropdown
                                    value={country}
                                    onChange={(val) => {
                                        setCountry(val);
                                        // Clear region when country changes
                                        setProvince('');
                                        // Update phone input country
                                        setMobile('');
                                    }}
                                    classes={`form-control ${validated && formErrors.country ? 'is-invalid' : ''} 
                                             ${validated && !formErrors.country ? 'is-valid' : ''}`}
                                    required
                                    priorityOptions={["South Africa"]}
                                    defaultOptionLabel="Select Country"
                                    style={getBorderStyle('country')}
                                />
                                {validated && formErrors.country && (
                                    <div className="invalid-feedback d-block">
                                        <div className="d-flex align-items-center">
                                            <FaExclamationCircle className="me-1" /> Country is required
                                        </div>
                                    </div>
                                )}
                            </FormGroup>

                            <FormGroup controlId='mobile'>
                                <FormLabel className='my-2 fw-semibold'>Mobile Phone</FormLabel>
                                <div className={validated && formErrors.mobile ? 'is-invalid' : ''}>
                                    <PhoneInput
                                        required
                                        value={mobile}
                                        onChange={setMobile}
                                        country={getCountryIsoCode(country)}
                                        countryCodeEditable={true}
                                        inputClass='w-100'
                                        inputStyle={{ 
                                            height: '3rem',
                                            ...(validated && formErrors.mobile ? { borderColor: errorColor, boxShadow: `0 0 0 0.25rem rgba(220, 53, 69, 0.25)` } : {}),
                                            ...(validated && !formErrors.mobile ? { borderColor: 'green', boxShadow: `0 0 0 0.25rem rgba(40, 167, 69, 0.25)` } : {})
                                        }}
                                        preferredCountries={['za']}
                                    />
                                </div>
                                {validated && formErrors.mobile && (
                                    <div className="invalid-feedback d-block">
                                        <div className="d-flex align-items-center">
                                            <FaExclamationCircle className="me-1" /> Mobile number is required
                                        </div>
                                    </div>
                                )}
                            </FormGroup>

                            <div className="mb-3"></div>

                            <FormGroup controlId='province'>
                               
                                <RegionDropdown
                                    country={country}
                                    value={province}
                                    onChange={(val) => setProvince(val)}
                                    classes={`form-control ${validated && formErrors.province ? 'is-invalid' : ''} 
                                             ${validated && !formErrors.province ? 'is-valid' : ''}`}
                                    required
                                    disableWhenEmpty={true}
                                    defaultOptionLabel="Select Region"
                                    style={getBorderStyle('province')}
                                />
                                {validated && formErrors.province && (
                                    <div className="invalid-feedback d-block">
                                        <div className="d-flex align-items-center">
                                            <FaExclamationCircle className="me-1" /> Region is required
                                        </div>
                                    </div>
                                )}
                            </FormGroup>

                            <FormGroup controlId='district'>
                                <FormLabel className='my-2 fw-semibold'>City or Suburb</FormLabel>
                                <FormControl
                                    required
                                    type='text'
                                    placeholder='Enter City/Suburb'
                                    value={district}
                                    onChange={(e) => setDistrict(e.target.value)}
                                    isInvalid={validated && formErrors.district}
                                    isValid={validated && !formErrors.district}
                                    style={getBorderStyle('district')}
                                />
                                <Form.Control.Feedback type="invalid">
                                    <div className="d-flex align-items-center">
                                        <FaExclamationCircle className="me-1" /> City orSuburb is required
                                    </div>
                                </Form.Control.Feedback>
                            </FormGroup>

                            <FormGroup controlId='postalCode'>
                                <FormLabel className='my-2 fw-semibold'>Postal Code</FormLabel>
                                <FormControl
                                    required
                                    type='text'
                                    pattern="[0-9]*"
                                    placeholder='Enter Postal Code'
                                    value={postalCode}
                                    onChange={(e) => setPostalCode(e.target.value)}
                                    isInvalid={validated && formErrors.postalCode}
                                    isValid={validated && !formErrors.postalCode}
                                    style={getBorderStyle('postalCode')}
                                />
                                <Form.Control.Feedback type="invalid">
                                    <div className="d-flex align-items-center">
                                        <FaExclamationCircle className="me-1" /> Postal code is required
                                    </div>
                                </Form.Control.Feedback>
                            </FormGroup>

                            <Row className='d-flex justify-content-between mt-3'>
                                <Col xs={6} className='d-flex justify-content-center'>
                                    <Button 
                                        type='submit' 
                                        style={{ backgroundColor: sageGreen, borderColor: warmConcrete }}
                                        className='my-3 w-100'
                                    >
                                        <FaPen size={14} className="me-2" /> Update
                                    </Button>
                                </Col>
                                
                            </Row>
                        </Form>
                    </UpdateProfileForm>
                </Modal.Body>
            </Modal>
            {showDeleteModal && (
                <DeleteAddressModal
                    show={showDeleteModal}
                    onHide={() => setShowDeleteModal(false)}
                    addressData={addressData}
                    dispatch={dispatch}
                />
            )}
        </>
    );
}

export default UpdateAddressModal;